import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';

import Swal from 'sweetalert2';
import { Subject, takeUntil } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';

import { PointOfSaleService } from '../../../../services/pos';
import { PetServices } from '../../../../services/petservices';
import { LanguageServiceService } from '../../../../services/language-service.service';
import { environment } from '../../../../../environments/environment';

interface ClinicThemePalette {
  primary: string;
  primaryDark: string;
  primaryLight: string;
  secondary: string;
  accent: string;
  textOnPrimary: string;
  primaryRgb: string;
  secondaryRgb: string;
  accentRgb: string;
}

interface ClinicThemeColor {
  r: number;
  g: number;
  b: number;
}

interface ClinicThemeColorBucket extends ClinicThemeColor {
  count: number;
  saturation: number;
  luminance: number;
  score: number;
  isMonochrome: boolean;
}

interface ClinicThemeStyleMap {
  [key: string]: string;
}

@Component({
  selector: 'app-bbclinic',
  templateUrl: './bbclinic.component.html',
  styleUrl: './bbclinic.component.scss'
})
export class BbclinicComponent implements OnInit, OnDestroy {
  warehouseId: number | null = null;

  clinic: any = null;
  services: any[] = [];
  selectedService: any = null;

  loadingClinic = false;
  loadingServices = false;
  loadingMoreServices = false;
  initialPageLoading = true;
  initialServicesReady = false;
  initialThemeReady = false;
  bookingLoading = false;

  pageError = '';
  servicesError = '';
  bookingError = '';

  readonly defaultImage = 'assets/logo-01.png';
  clinicThemeStyles: ClinicThemeStyleMap = {};
  currentLanguage = this.getStoredLanguage();
  languageDropdownOpen = false;

  isAr = this.currentLanguage === 'ar';

  private lastProcessedLogoUrl = '';
  private themeRequestId = 0;
  private destroyed = false;
  private readonly destroy$ = new Subject<void>();
  private readonly themeCacheVersion = 'v2';

  minBookingDate = this.formatDateTimeForInput(new Date());
  todayDateOnly = this.formatDateOnly(new Date());
  bookingDateOnly = '';
  bookingTime = '10:00';
  showBookingCalendar = false;
  bookingCalendarViewDate = new Date();
  readonly hourOptions = Array.from({ length: 12 }, (_, index) => index + 1);
  readonly minuteOptions = Array.from({ length: 60 }, (_, index) =>
    index.toString().padStart(2, '0')
  );
  readonly servicesPerPage = 5;
  servicesPage = 1;
  hasMoreServices = true;
  totalServices = 0;

  bookingForm: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private posService: PointOfSaleService,
    private petServices: PetServices,
    private languageService: LanguageServiceService,
    private transloco: TranslocoService
  ) {
    this.bookingForm = this.fb.group({
      pet_service_id: ['', Validators.required],

      client_name: [
        '',
        [
          Validators.required,
          Validators.minLength(2)
        ]
      ],

      phone_number: [
        '',
        [
          Validators.required,
          Validators.pattern(/^\+?[0-9]{8,15}$/)
        ]
      ],

      pet_type: [''],

      booking_date: [
        '',
        Validators.required
      ],

      warehouse_id: [
        null,
        Validators.required
      ]
    });
  }

  ngOnInit(): void {
    this.languageService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => {
        this.currentLanguage = language;
        this.isAr = language === 'ar';
        this.transloco.setActiveLang(language);
      });

    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId || !/^\d+$/.test(routeId)) {
      this.pageError = this.t('bbClinic.errors.invalidClinicLink');
      this.initialPageLoading = false;

      return;
    }

    this.warehouseId = Number(routeId);

    if (
      !Number.isInteger(this.warehouseId) ||
      this.warehouseId <= 0
    ) {
      this.pageError = this.t('bbClinic.errors.invalidClinicId');
      this.initialPageLoading = false;

      return;
    }

    this.bookingForm.patchValue({
      warehouse_id: this.warehouseId
    });

    this.loadClinic();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.themeRequestId++;
    this.destroy$.next();
    this.destroy$.complete();
  }

  get loading(): boolean {
    return this.initialPageLoading;
  }

  private finishInitialPageLoadingIfReady(): void {
    if (this.initialServicesReady && this.initialThemeReady) {
      this.initialPageLoading = false;
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.loadMoreServicesOnScroll();
  }

  toggleLanguageDropdown(event?: Event): void {
    event?.stopPropagation();
    this.languageDropdownOpen = !this.languageDropdownOpen;
  }

  setLanguage(language: string, event?: Event): void {
    event?.stopPropagation();
    this.languageService.changeLanguage(language);
    this.transloco.setActiveLang(language);
    this.currentLanguage = language;
    this.isAr = language === 'ar';
    this.languageDropdownOpen = false;
  }

  toggleBookingCalendar(): void {
    this.showBookingCalendar = !this.showBookingCalendar;

    if (this.showBookingCalendar) {
      const selectedDate = this.parseDateOnly(this.bookingDateOnly);
      const baseDate = selectedDate || new Date();
      this.bookingCalendarViewDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    }
  }

  closeBookingCalendar(): void {
    this.showBookingCalendar = false;
  }

  previousBookingCalendarMonth(): void {
    this.bookingCalendarViewDate = new Date(
      this.bookingCalendarViewDate.getFullYear(),
      this.bookingCalendarViewDate.getMonth() - 1,
      1
    );
  }

  nextBookingCalendarMonth(): void {
    this.bookingCalendarViewDate = new Date(
      this.bookingCalendarViewDate.getFullYear(),
      this.bookingCalendarViewDate.getMonth() + 1,
      1
    );
  }

  getBookingCalendarTitle(): string {
    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      month: 'long',
      year: 'numeric',
    }).format(this.bookingCalendarViewDate);
  }

  getBookingCalendarWeekDays(): string[] {
    return ['bbClinic.days.sun', 'bbClinic.days.mon', 'bbClinic.days.tue', 'bbClinic.days.wed', 'bbClinic.days.thu', 'bbClinic.days.fri', 'bbClinic.days.sat'].map(key => this.t(key));
  }

  getBookingCalendarDays(): Array<{
    date: Date;
    dayNumber: string;
    isCurrentMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isDisabled: boolean;
  }> {
    const year = this.bookingCalendarViewDate.getFullYear();
    const month = this.bookingCalendarViewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - firstDay.getDay());
    const today = this.parseDateOnly(this.todayDateOnly) || new Date();
    today.setHours(0, 0, 0, 0);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      date.setHours(0, 0, 0, 0);

      return {
        date,
        dayNumber: this.isAr ? this.toArabicDigits(date.getDate()) : String(date.getDate()),
        isCurrentMonth: date.getMonth() === month,
        isToday: this.isSameDate(date, today),
        isSelected: this.bookingDateOnly === this.formatDateOnly(date),
        isDisabled: date < today,
      };
    });
  }

  selectBookingCalendarDate(date: Date): void {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    const today = this.parseDateOnly(this.todayDateOnly) || new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) return;

    this.bookingDateOnly = this.formatDateOnly(selectedDate);
    this.syncBookingDateTime();
    this.closeBookingCalendar();
  }

  getBookingDateDisplay(): string {
    const date = this.parseDateOnly(this.bookingDateOnly);

    if (!date) {
      return this.t('bbClinic.form.selectBookingDate');
    }

    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  updateBookingTimePart(part: 'hour' | 'minute' | 'period', event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    const [hourText, minuteText] = (this.bookingTime || '10:00').split(':');
    const currentHour = Number(hourText);
    let hour12 = this.getTimeHour12(this.bookingTime);
    let minute = minuteText || '00';
    let period: 'am' | 'pm' = !Number.isNaN(currentHour) && currentHour >= 12 ? 'pm' : 'am';

    if (part === 'hour') {
      hour12 = Number(value);
    }

    if (part === 'minute') {
      minute = value.padStart(2, '0');
    }

    if (part === 'period') {
      period = value as 'am' | 'pm';
    }

    this.bookingTime = `${this.to24Hour(hour12, period)}:${minute}`;
    this.syncBookingDateTime();
  }

  isSelectedBookingTimeValid(): boolean {
    if (!this.bookingDateOnly || !this.bookingTime) return false;

    const selectedDateTime = new Date(`${this.bookingDateOnly}T${this.bookingTime}`);
    return !Number.isNaN(selectedDateTime.getTime()) && selectedDateTime.getTime() > Date.now();
  }

  formatTime(time: string): string {
    if (!time) return '';

    const [hourText, minuteText] = time.split(':');
    const hour = Number(hourText);
    const minute = Number(minuteText);

    if (Number.isNaN(hour) || Number.isNaN(minute)) return time;

    const hour12 = hour % 12 || 12;
    const period = hour >= 12 ? 'PM' : 'AM';
    const label = `${hour12}:${String(minute).padStart(2, '0')} ${period}`;

    if (!this.isAr) return label;

    return label.replace('AM', this.t('bbClinic.time.amShort')).replace('PM', this.t('bbClinic.time.pmShort'));
  }

  getTimeHour12(time: string): number {
    const [hourText] = (time || '10:00').split(':');
    const hour = Number(hourText);
    if (Number.isNaN(hour)) return 10;
    return hour % 12 || 12;
  }

  getTimeMinute(time: string): string {
    const [, minuteText] = (time || '10:00').split(':');
    return (minuteText || '00').padStart(2, '0');
  }

  getTimePeriod(time: string): 'am' | 'pm' {
    const [hourText] = (time || '10:00').split(':');
    const hour = Number(hourText);
    return !Number.isNaN(hour) && hour >= 12 ? 'pm' : 'am';
  }

  getTimeOptionLabel(value: number | string): string {
    return String(value);
  }

  getPeriodOptionLabel(period: 'am' | 'pm'): string {
    return period.toUpperCase();
  }

  // =========================
  // Load clinic
  // =========================

  loadClinic(): void {
    if (!this.warehouseId) {
      return;
    }

    this.loadingClinic = true;
    this.initialPageLoading = true;
    this.initialServicesReady = false;
    this.initialThemeReady = false;
    this.pageError = '';
    this.servicesError = '';
    this.clinic = null;
    this.lastProcessedLogoUrl = '';
    this.themeRequestId++;
    this.resetClinicTheme();

    this.posService
      .getPublicClinic(this.warehouseId)
      .subscribe({
        next: (response: any) => {
          console.log('Public clinic response:', response);

          this.clinic = this.extractClinic(response);
          this.loadingClinic = false;

          if (!this.clinic) {
            this.pageError = this.t('bbClinic.errors.clinicNotFound');
            this.initialPageLoading = false;

            return;
          }

          void this.applyThemeFromClinicLogo(this.getClinicThemeLogoUrl()).finally(() => {
            if (this.destroyed) {
              return;
            }

            this.initialThemeReady = true;
            this.finishInitialPageLoadingIfReady();
          });
          this.loadServices();
        },

        error: (error: any) => {
          console.error(
            'Failed to load clinic:',
            error
          );

          this.loadingClinic = false;
          this.initialPageLoading = false;
          this.initialServicesReady = true;
          this.initialThemeReady = true;

          this.pageError =
            error?.error?.message ||
            error?.message ||
            this.t('bbClinic.errors.loadClinicFailed');
        }
      });
  }

  private extractClinic(response: any): any {
    if (!response) {
      return null;
    }

    if (response?.warehouse) {
      return response.warehouse;
    }

    if (response?.clinic) {
      return response.clinic;
    }

    if (response?.data?.warehouse) {
      return response.data.warehouse;
    }

    if (response?.data?.clinic) {
      return response.data.clinic;
    }

    if (
      response?.data &&
      !Array.isArray(response.data)
    ) {
      return response.data;
    }

    if (Array.isArray(response)) {
      return response[0] || null;
    }

    return response;
  }

  async applyThemeFromClinicLogo(logoUrl: string): Promise<void> {
    const normalizedLogoUrl = String(logoUrl || '').trim();

    if (
      !normalizedLogoUrl ||
      this.isFallbackClinicLogo(normalizedLogoUrl) ||
      this.lastProcessedLogoUrl === normalizedLogoUrl ||
      !this.canUseBrowserThemeApis()
    ) {
      return;
    }

    this.lastProcessedLogoUrl = normalizedLogoUrl;
    const requestId = ++this.themeRequestId;
    const cachedPalette = this.readCachedClinicTheme(normalizedLogoUrl);

    if (cachedPalette) {
      this.applyClinicThemePalette(cachedPalette, requestId);
      return;
    }

    try {
      const palette = await this.extractPaletteFromImage(normalizedLogoUrl);

      if (!palette || this.destroyed || requestId !== this.themeRequestId) {
        return;
      }

      this.writeCachedClinicTheme(normalizedLogoUrl, palette);
      this.applyClinicThemePalette(palette, requestId);
    } catch (error) {
      this.warnClinicTheme('Unable to extract clinic theme from logo.', error);
      this.resetClinicTheme(requestId);
    }
  }

  private getClinicThemeLogoUrl(): string {
    return String(this.clinic?.image_url || '/assets/logo-01.png').trim();
  }

  private resetClinicTheme(requestId = this.themeRequestId): void {
    if (this.destroyed || requestId !== this.themeRequestId) {
      return;
    }

    this.clinicThemeStyles = {};
  }

  private applyClinicThemePalette(
    palette: ClinicThemePalette,
    requestId: number
  ): void {
    if (this.destroyed || requestId !== this.themeRequestId) {
      return;
    }

    this.clinicThemeStyles = {
      '--clinic-navy': palette.primaryDark,
      '--clinic-blue': palette.primary,
      '--clinic-cyan': palette.accent,
      '--clinic-primary-light': palette.primaryLight,
      '--clinic-primary-rgb': palette.primaryRgb,
      '--clinic-secondary-rgb': palette.secondaryRgb,
      '--clinic-accent-rgb': palette.accentRgb,
      '--clinic-on-primary': palette.textOnPrimary
    };
  }

  private async extractPaletteFromImage(
    logoUrl: string
  ): Promise<ClinicThemePalette | null> {
    const image = await this.loadThemeImage(logoUrl);
    const canvas = document.createElement('canvas');
    const size = 80;
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d', {
      willReadFrequently: true
    });

    if (!context) {
      return null;
    }

    context.clearRect(0, 0, size, size);
    context.drawImage(image, 0, 0, size, size);

    let imageData: ImageData;

    try {
      imageData = context.getImageData(0, 0, size, size);
    } catch (error) {
      this.warnClinicTheme('Clinic logo canvas is not readable.', error);
      return null;
    }

    const buckets = this.buildThemeColorBuckets(imageData.data, false);
    const usableBuckets = buckets.length
      ? buckets
      : this.buildThemeColorBuckets(imageData.data, true);

    if (!usableBuckets.length) {
      return null;
    }

    return this.createThemePalette(usableBuckets);
  }

  private loadThemeImage(logoUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      const timeout = window.setTimeout(() => {
        image.onload = null;
        image.onerror = null;
        reject(new Error('Clinic logo theme extraction timed out.'));
      }, 4000);

      image.onload = () => {
        window.clearTimeout(timeout);
        resolve(image);
      };
      image.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error('Clinic logo failed to load.'));
      };
      image.src = logoUrl;
    });
  }

  private buildThemeColorBuckets(
    data: Uint8ClampedArray,
    allowMonochrome: boolean
  ): ClinicThemeColorBucket[] {
    const quantizationStep = 24;
    const buckets = new Map<string, ClinicThemeColorBucket>();

    for (let index = 0; index < data.length; index += 8) {
      const alpha = data[index + 3];
      if (alpha < 160) continue;

      const color = {
        r: this.quantizeColorValue(data[index], quantizationStep),
        g: this.quantizeColorValue(data[index + 1], quantizationStep),
        b: this.quantizeColorValue(data[index + 2], quantizationStep)
      };

      if (!this.isUsableThemeColor(color, allowMonochrome)) {
        continue;
      }

      const key = `${color.r}-${color.g}-${color.b}`;
      const existing = buckets.get(key);

      if (existing) {
        existing.count++;
        continue;
      }

      const hsl = this.rgbToHsl(color);
      const luminance = this.relativeLuminance(color);
      buckets.set(key, {
        ...color,
        count: 1,
        saturation: hsl.s,
        luminance,
        score: 0,
        isMonochrome: hsl.s < 0.12
      });
    }

    const maxCount = Math.max(
      1,
      ...Array.from(buckets.values()).map(bucket => bucket.count)
    );

    return Array.from(buckets.values())
      .filter(bucket => bucket.count >= 2)
      .map(bucket => ({
        ...bucket,
        score: bucket.isMonochrome
          ? bucket.count / maxCount + (1 - Math.abs(bucket.luminance - 0.22)) * 0.9
          : bucket.count / maxCount +
            bucket.saturation * 1.4 +
            this.getVibranceScore(bucket) * 0.9
      }))
      .sort((first, second) => second.score - first.score);
  }

  private createThemePalette(
    buckets: ClinicThemeColorBucket[]
  ): ClinicThemePalette {
    const isMonochromeTheme = buckets.every(bucket => bucket.isMonochrome);
    const primary = isMonochromeTheme
      ? this.normalizeMonochromePrimaryColor(buckets[0])
      : this.normalizePrimaryThemeColor(buckets[0]);
    const secondary = isMonochromeTheme
      ? this.hslToRgb(220, 0.12, 0.18)
      : this.findDistinctThemeColor(buckets, primary, 82) ||
        this.rotateColorHue(primary, 24, 0.58);
    const accent = isMonochromeTheme
      ? this.hslToRgb(204, 0.68, 0.48)
      : this.findDistinctThemeColor(buckets, primary, 128, secondary) ||
        this.rotateColorHue(primary, -34, 0.72);
    const normalizedAccent = this.normalizeAccentThemeColor(accent, primary);
    const primaryDark = this.createPrimaryDarkColor(primary, isMonochromeTheme);
    const primaryLight = this.mixColors(primary, { r: 255, g: 255, b: 255 }, 0.88);
    const textOnPrimary = this.getReadableGradientTextColor(primary, primaryDark);
    const accessiblePrimaryDark = this.ensureGradientContrast(primary, primaryDark, textOnPrimary);

    return {
      primary: this.rgbToHex(primary),
      primaryDark: this.rgbToHex(accessiblePrimaryDark),
      primaryLight: this.rgbToHex(primaryLight),
      secondary: this.rgbToHex(secondary),
      accent: this.rgbToHex(normalizedAccent),
      textOnPrimary,
      primaryRgb: this.rgbToString(primary),
      secondaryRgb: this.rgbToString(secondary),
      accentRgb: this.rgbToString(normalizedAccent)
    };
  }

  private normalizeMonochromePrimaryColor(
    color: ClinicThemeColor
  ): ClinicThemeColor {
    const hsl = this.rgbToHsl(color);
    const lightness = hsl.l < 0.18 ? 0.22 : this.clamp(hsl.l, 0.2, 0.38);
    return this.hslToRgb(220, 0.12, lightness);
  }

  private normalizePrimaryThemeColor(color: ClinicThemeColor): ClinicThemeColor {
    const hsl = this.rgbToHsl(color);

    if (hsl.l > 0.72) {
      return this.hslToRgb(hsl.h, Math.max(hsl.s, 0.52), 0.48);
    }

    if (hsl.l < 0.18) {
      return this.hslToRgb(hsl.h, Math.max(hsl.s, 0.5), 0.34);
    }

    if (hsl.s < 0.22) {
      return this.hslToRgb(212, 0.72, 0.42);
    }

    return this.clampColor(color);
  }

  private normalizeAccentThemeColor(
    color: ClinicThemeColor,
    primary: ClinicThemeColor
  ): ClinicThemeColor {
    if (this.colorDistance(color, primary) >= 74) {
      return this.clampColor(color);
    }

    return this.rotateColorHue(primary, -42, 0.72);
  }

  private findDistinctThemeColor(
    buckets: ClinicThemeColorBucket[],
    primary: ClinicThemeColor,
    minimumDistance: number,
    avoidColor?: ClinicThemeColor
  ): ClinicThemeColor | null {
    const bucket = buckets.find(candidate => {
      const distanceFromPrimary = this.colorDistance(candidate, primary);
      const distanceFromAvoid = avoidColor
        ? this.colorDistance(candidate, avoidColor)
        : Number.POSITIVE_INFINITY;

      return distanceFromPrimary >= minimumDistance && distanceFromAvoid >= 70;
    });

    return bucket ? this.clampColor(bucket) : null;
  }

  private createPrimaryDarkColor(
    color: ClinicThemeColor,
    isMonochromeTheme: boolean
  ): ClinicThemeColor {
    if (isMonochromeTheme) {
      return this.hslToRgb(220, 0.14, 0.14);
    }

    const hsl = this.rgbToHsl(color);
    return this.hslToRgb(
      hsl.h,
      this.clamp(Math.max(hsl.s, 0.42), 0, 1),
      this.clamp(Math.min(hsl.l * 0.58, 0.28), 0.16, 0.3)
    );
  }

  private ensureGradientContrast(
    primary: ClinicThemeColor,
    primaryDark: ClinicThemeColor,
    textColor: string
  ): ClinicThemeColor {
    const foreground = this.hexToRgb(textColor);
    if (
      this.contrastRatio(primary, foreground) >= 4.5 &&
      this.contrastRatio(primaryDark, foreground) >= 4.5
    ) {
      return primaryDark;
    }

    const hsl = this.rgbToHsl(primaryDark);
    return this.hslToRgb(hsl.h, hsl.s, textColor === '#ffffff' ? 0.18 : 0.78);
  }

  private isUsableThemeColor(
    color: ClinicThemeColor,
    allowMonochrome: boolean
  ): boolean {
    const hsl = this.rgbToHsl(color);
    const luminance = this.relativeLuminance(color);
    const maxChannel = Math.max(color.r, color.g, color.b);
    const minChannel = Math.min(color.r, color.g, color.b);

    if (maxChannel > 238 && minChannel > 218) return false;
    if (hsl.l > 0.86) return false;
    if (hsl.l < 0.08) return false;
    if (!allowMonochrome && hsl.s < 0.08) return false;
    if (allowMonochrome && hsl.s < 0.12) {
      return luminance >= 0.02 && luminance <= 0.62;
    }
    if (hsl.s < 0.12 && luminance < 0.2) return false;
    if (hsl.s < 0.08) return false;

    return true;
  }

  private getVibranceScore(color: ClinicThemeColor): number {
    const hsl = this.rgbToHsl(color);
    return hsl.s * (1 - Math.abs(hsl.l - 0.5));
  }

  private getReadableGradientTextColor(
    primary: ClinicThemeColor,
    primaryDark: ClinicThemeColor
  ): string {
    const white = { r: 255, g: 255, b: 255 };
    const dark = { r: 7, g: 27, b: 61 };
    const whiteMinimum = Math.min(
      this.contrastRatio(primary, white),
      this.contrastRatio(primaryDark, white)
    );
    const darkMinimum = Math.min(
      this.contrastRatio(primary, dark),
      this.contrastRatio(primaryDark, dark)
    );

    return whiteMinimum >= darkMinimum ? '#ffffff' : '#071b3d';
  }

  private contrastRatio(first: ClinicThemeColor, second: ClinicThemeColor): number {
    const firstLuminance = this.relativeLuminance(first);
    const secondLuminance = this.relativeLuminance(second);
    const lighter = Math.max(firstLuminance, secondLuminance);
    const darker = Math.min(firstLuminance, secondLuminance);
    return (lighter + 0.05) / (darker + 0.05);
  }

  private relativeLuminance(color: ClinicThemeColor): number {
    const channels = [color.r, color.g, color.b].map(value => {
      const normalized = this.clamp(value, 0, 255) / 255;
      return normalized <= 0.03928
        ? normalized / 12.92
        : Math.pow((normalized + 0.055) / 1.055, 2.4);
    });

    return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  }

  private colorDistance(first: ClinicThemeColor, second: ClinicThemeColor): number {
    return Math.sqrt(
      Math.pow(first.r - second.r, 2) +
      Math.pow(first.g - second.g, 2) +
      Math.pow(first.b - second.b, 2)
    );
  }

  private rotateColorHue(
    color: ClinicThemeColor,
    degrees: number,
    saturation: number
  ): ClinicThemeColor {
    const hsl = this.rgbToHsl(color);
    return this.hslToRgb((hsl.h + degrees + 360) % 360, Math.max(hsl.s, saturation), 0.5);
  }

  private mixColors(
    first: ClinicThemeColor,
    second: ClinicThemeColor,
    secondWeight: number
  ): ClinicThemeColor {
    const weight = this.clamp(secondWeight, 0, 1);
    return {
      r: this.clampRgb(first.r * (1 - weight) + second.r * weight),
      g: this.clampRgb(first.g * (1 - weight) + second.g * weight),
      b: this.clampRgb(first.b * (1 - weight) + second.b * weight)
    };
  }

  private rgbToHsl(color: ClinicThemeColor): { h: number; s: number; l: number } {
    const r = this.clamp(color.r, 0, 255) / 255;
    const g = this.clamp(color.g, 0, 255) / 255;
    const b = this.clamp(color.b, 0, 255) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const lightness = (max + min) / 2;

    if (max === min) {
      return { h: 0, s: 0, l: lightness };
    }

    const delta = max - min;
    const saturation = lightness > 0.5
      ? delta / (2 - max - min)
      : delta / (max + min);
    let hue = 0;

    if (max === r) {
      hue = (g - b) / delta + (g < b ? 6 : 0);
    } else if (max === g) {
      hue = (b - r) / delta + 2;
    } else {
      hue = (r - g) / delta + 4;
    }

    return {
      h: hue * 60,
      s: saturation,
      l: lightness
    };
  }

  private hslToRgb(hue: number, saturation: number, lightness: number): ClinicThemeColor {
    const h = ((hue % 360) + 360) % 360 / 360;
    const s = this.clamp(saturation, 0, 1);
    const l = this.clamp(lightness, 0, 1);

    if (s === 0) {
      const value = this.clampRgb(l * 255);
      return { r: value, g: value, b: value };
    }

    const hueToRgb = (p: number, q: number, tValue: number): number => {
      let t = tValue;
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    return {
      r: this.clampRgb(hueToRgb(p, q, h + 1 / 3) * 255),
      g: this.clampRgb(hueToRgb(p, q, h) * 255),
      b: this.clampRgb(hueToRgb(p, q, h - 1 / 3) * 255)
    };
  }

  private rgbToHex(color: ClinicThemeColor): string {
    const toHex = (value: number): string =>
      this.clampRgb(value).toString(16).padStart(2, '0');

    return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`;
  }

  private hexToRgb(hex: string): ClinicThemeColor {
    const normalized = hex.replace('#', '');
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16)
    };
  }

  private rgbToString(color: ClinicThemeColor): string {
    const clamped = this.clampColor(color);
    return `${clamped.r}, ${clamped.g}, ${clamped.b}`;
  }

  private clampColor(color: ClinicThemeColor): ClinicThemeColor {
    return {
      r: this.clampRgb(color.r),
      g: this.clampRgb(color.g),
      b: this.clampRgb(color.b)
    };
  }

  private quantizeColorValue(value: number, step: number): number {
    return this.clampRgb(Math.round(value / step) * step);
  }

  private clampRgb(value: number): number {
    return Math.round(this.clamp(value, 0, 255));
  }

  private clamp(value: number, minimum: number, maximum: number): number {
    return Math.min(maximum, Math.max(minimum, value));
  }

  private readCachedClinicTheme(logoUrl: string): ClinicThemePalette | null {
    const storage = this.getThemeStorage();
    if (!storage || !this.warehouseId) return null;

    try {
      const rawValue = storage.getItem(this.getClinicThemeCacheKey(logoUrl));
      if (!rawValue) return null;

      const parsed = JSON.parse(rawValue) as {
        logoUrl?: unknown;
        palette?: unknown;
      };

      if (parsed.logoUrl !== logoUrl || !this.isValidClinicThemePalette(parsed.palette)) {
        storage.removeItem(this.getClinicThemeCacheKey(logoUrl));
        return null;
      }

      return parsed.palette;
    } catch (error) {
      this.warnClinicTheme('Invalid cached clinic theme ignored.', error);
      return null;
    }
  }

  private writeCachedClinicTheme(
    logoUrl: string,
    palette: ClinicThemePalette
  ): void {
    const storage = this.getThemeStorage();
    if (!storage || !this.warehouseId) return;

    try {
      storage.setItem(
        this.getClinicThemeCacheKey(logoUrl),
        JSON.stringify({ logoUrl, palette })
      );
    } catch (error) {
      this.warnClinicTheme('Clinic theme cache could not be written.', error);
    }
  }

  private isValidClinicThemePalette(value: unknown): value is ClinicThemePalette {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const palette = value as Partial<ClinicThemePalette>;
    const hexPattern = /^#[0-9a-f]{6}$/i;

    return [
      palette.primary,
      palette.primaryDark,
      palette.primaryLight,
      palette.secondary,
      palette.accent,
      palette.textOnPrimary
    ].every(color => typeof color === 'string' && hexPattern.test(color)) &&
      [palette.primaryRgb, palette.secondaryRgb, palette.accentRgb]
        .every(rgb => typeof rgb === 'string' && this.isValidRgbString(rgb));
  }

  private isValidRgbString(value: string): boolean {
    const parts = value.split(',').map(part => Number(part.trim()));
    return parts.length === 3 &&
      parts.every(part => Number.isInteger(part) && part >= 0 && part <= 255);
  }

  private getClinicThemeCacheKey(logoUrl: string): string {
    return `bbclinic-theme-${this.themeCacheVersion}-${this.warehouseId}-${logoUrl}`;
  }

  private getThemeStorage(): Storage | null {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }

    return window.sessionStorage;
  }

  private canUseBrowserThemeApis(): boolean {
    return typeof window !== 'undefined' &&
      typeof document !== 'undefined' &&
      typeof Image !== 'undefined';
  }

  private isFallbackClinicLogo(logoUrl: string): boolean {
    const normalized = logoUrl.replace(/^\/+/, '').toLowerCase();
    return normalized === 'assets/logo-01.png' ||
      normalized.endsWith('/assets/logo-01.png');
  }

  private getStoredLanguage(): string {
    if (typeof window === 'undefined' || !window.localStorage) {
      return 'en';
    }

    return window.localStorage.getItem('language') || 'en';
  }

  private t(key: string, params?: Record<string, string | number>): string {
    return this.transloco.translate(key, params);
  }

  private warnClinicTheme(message: string, error?: unknown): void {
    const isProduction =
      (environment as { production?: boolean }).production === true;

    if (!isProduction && typeof console !== 'undefined') {
      console.warn(message, error);
    }
  }

  // =========================
  // Load services
  // =========================

  loadServices(reset = true): void {
    if (!this.warehouseId) {
      return;
    }

    if (reset) {
      this.loadingServices = true;
      this.servicesError = '';
      this.services = [];
      this.selectedService = null;
      this.servicesPage = 1;
      this.hasMoreServices = true;
      this.totalServices = 0;
    } else {
      if (
        this.loadingServices ||
        this.loadingMoreServices ||
        !this.hasMoreServices
      ) {
        return;
      }

      this.loadingMoreServices = true;
    }

    this.petServices
      .getPublicByWarehouse(
        this.warehouseId,
        this.servicesPage,
        this.servicesPerPage
      )
      .subscribe({
        next: (response: any) => {
          console.log(
            'Public clinic services response:',
            response
          );

          const pageServices = this.extractServices(response);
          this.services = reset
            ? pageServices
            : this.mergeServices(this.services, pageServices);
          this.updateServicesPagination(response, pageServices.length);
          this.loadingServices = false;
          this.loadingMoreServices = false;
          if (reset) {
            this.initialServicesReady = true;
            this.finishInitialPageLoadingIfReady();
          }
        },

        error: (error: any) => {
          console.error(
            'Failed to load clinic services:',
            error
          );

          this.loadingServices = false;
          this.loadingMoreServices = false;
          if (reset) {
            this.initialServicesReady = true;
            this.finishInitialPageLoadingIfReady();
          }
          if (!reset && this.servicesPage > 1) {
            this.servicesPage--;
          }

          this.servicesError =
            error?.error?.message ||
            error?.message ||
            this.t('bbClinic.errors.loadServicesFailed');
        }
      });
  }

  loadMoreServices(): void {
    if (!this.hasMoreServices || this.loadingServices || this.loadingMoreServices) {
      return;
    }

    this.servicesPage++;
    this.loadServices(false);
  }

  private loadMoreServicesOnScroll(): void {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      !this.clinic ||
      this.pageError ||
      this.servicesError ||
      !this.hasMoreServices ||
      this.loadingServices ||
      this.loadingMoreServices
    ) {
      return;
    }

    const servicesSection = document.getElementById('services-section');
    if (!servicesSection) {
      return;
    }

    const sectionRect = servicesSection.getBoundingClientRect();
    const distanceFromSectionEnd = sectionRect.bottom - window.innerHeight;

    if (distanceFromSectionEnd <= 520) {
      this.loadMoreServices();
    }
  }

  private updateServicesPagination(
    response: any,
    fetchedCount: number
  ): void {
    const total = this.extractServicesTotal(response);
    const lastPage = this.extractServicesLastPage(response);
    const currentPage = this.extractServicesCurrentPage(response) || this.servicesPage;

    this.totalServices = total || this.services.length;

    if (lastPage) {
      this.hasMoreServices = currentPage < lastPage;
      return;
    }

    if (total) {
      this.hasMoreServices = this.services.length < total;
      return;
    }

    this.hasMoreServices = fetchedCount >= this.servicesPerPage;
  }

  private extractServicesTotal(response: any): number {
    const total =
      response?.total ??
      response?.data?.total ??
      response?.meta?.total ??
      response?.data?.meta?.total;

    return Number(total) || 0;
  }

  private extractServicesCurrentPage(response: any): number {
    const page =
      response?.current_page ??
      response?.data?.current_page ??
      response?.meta?.current_page ??
      response?.data?.meta?.current_page;

    return Number(page) || 0;
  }

  private extractServicesLastPage(response: any): number {
    const lastPage =
      response?.last_page ??
      response?.data?.last_page ??
      response?.meta?.last_page ??
      response?.data?.meta?.last_page;

    return Number(lastPage) || 0;
  }

  private mergeServices(current: any[], incoming: any[]): any[] {
    const seen = new Set(
      current.map(service => String(service?.id ?? service))
    );
    const uniqueIncoming = incoming.filter(service => {
      const key = String(service?.id ?? service);
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });

    return [...current, ...uniqueIncoming];
  }

  private extractServices(response: any): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data?.data)) {
      return response.data.data;
    }

    if (Array.isArray(response?.data?.services)) {
      return response.data.services;
    }

    if (Array.isArray(response?.data?.pet_services)) {
      return response.data.pet_services;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.services)) {
      return response.services;
    }

    if (Array.isArray(response?.pet_services)) {
      return response.pet_services;
    }

    return [];
  }

  // =========================
  // Service selection
  // =========================

  selectService(service: any): void {
    if (!service?.id) {
      return;
    }

    this.selectedService = service;

    this.bookingForm.patchValue({
      pet_service_id: service.id
    });

    this.bookingForm
      .get('pet_service_id')
      ?.markAsTouched();

    this.scrollToBooking();
  }

  onServiceSelectChange(
    serviceId: string | number
  ): void {
    this.selectedService =
      this.services.find(
        service =>
          String(service?.id) === String(serviceId)
      ) || null;
  }

  isServiceSelected(service: any): boolean {
    return (
      String(
        this.bookingForm
          .get('pet_service_id')
          ?.value
      ) === String(service?.id)
    );
  }

  trackByServiceId(
    index: number,
    service: any
  ): number | string {
    return service?.id ?? index;
  }

  // =========================
  // Scroll
  // =========================

  scrollToBooking(): void {
    if (typeof document === 'undefined') {
      return;
    }

    setTimeout(() => {
      document
        .getElementById('booking-section')
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
    });
  }

  scrollToServices(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document
      .getElementById('services-section')
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
  }

  // =========================
  // Save booking
  // =========================

  saveBooking(): void {
    this.bookingError = '';

    if (!this.warehouseId) {
      this.bookingError = this.t('bbClinic.errors.clinicIdMissing');

      return;
    }

    if (this.bookingForm.invalid) {
      this.bookingForm.markAllAsTouched();

      Swal.fire({
        icon: 'warning',
        title: this.t('bbClinic.alerts.checkBookingTitle'),

        text: this.t('bbClinic.alerts.checkBookingText'),

        confirmButtonText: this.t('bbClinic.common.ok')
      });

      return;
    }

    if (!this.isSelectedBookingTimeValid()) {
      this.bookingForm
        .get('booking_date')
        ?.setErrors({
          pastDate: true
        });

      this.bookingForm
        .get('booking_date')
        ?.markAsTouched();

      return;
    }

    const raw = this.bookingForm.getRawValue();
    const selectedDate = new Date(raw.booking_date);

    if (
      Number.isNaN(selectedDate.getTime()) ||
      selectedDate.getTime() <= Date.now()
    ) {
      this.bookingForm
        .get('booking_date')
        ?.setErrors({
          pastDate: true
        });

      this.bookingForm
        .get('booking_date')
        ?.markAsTouched();

      return;
    }

    const payload = {
      pet_service_id:
        Number(raw.pet_service_id),

      client_name:
        String(raw.client_name).trim(),

      phone_number:
        String(raw.phone_number).trim(),

      pet_type:
        raw.pet_type || null,

      booking_date:
        this.normalizeDateTime(raw.booking_date),

      warehouse_id:
        this.warehouseId
    };

    this.bookingLoading = true;

    this.petServices
      .createPublicBooking(payload)
      .subscribe({
        next: () => {
          this.bookingLoading = false;

          Swal.fire({
            icon: 'success',

            title: this.t('bbClinic.alerts.bookingSuccessTitle'),

            text: this.t('bbClinic.alerts.bookingSuccessText'),

            confirmButtonText: this.t('bbClinic.common.ok')
          });

          this.resetBookingForm();
        },

        error: (error: any) => {
          console.error(
            'Failed to create booking:',
            error
          );

          this.bookingLoading = false;

          this.bookingError =
            error?.error?.message ||
            error?.message ||
            this.t('bbClinic.errors.submitBookingFailed');

          Swal.fire({
            icon: 'error',
            title: this.t('bbClinic.common.error'),

            text: this.bookingError,

            confirmButtonText: this.t('bbClinic.common.ok')
          });
        }
      });
  }

  resetBookingForm(): void {
    this.selectedService = null;
    this.bookingError = '';

    this.bookingForm.reset({
      pet_service_id: '',
      client_name: '',
      phone_number: '',
      pet_type: '',
      booking_date: '',
      warehouse_id: this.warehouseId
    });
    this.bookingDateOnly = '';
    this.bookingTime = '10:00';
    this.showBookingCalendar = false;

    this.minBookingDate =
      this.formatDateTimeForInput(new Date());
  }

  // =========================
  // Form errors
  // =========================

  shouldShowError(
    controlName: string
  ): boolean {
    const control =
      this.bookingForm.get(controlName);

    return !!control &&
      control.invalid &&
      control.touched;
  }

  getFieldError(
    controlName: string,
    fieldLabel: string
  ): string {
    const control =
      this.bookingForm.get(controlName);

    if (!control?.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.t('bbClinic.validation.required', { field: fieldLabel });
    }

    if (control.errors['minlength']) {
      return this.t('bbClinic.validation.minlength', { field: fieldLabel });
    }

    if (control.errors['pattern']) {
      return this.t('bbClinic.validation.phonePattern');
    }

    if (control.errors['pastDate']) {
      return this.t('bbClinic.validation.futureDate');
    }

    return this.t('bbClinic.validation.invalid', { field: fieldLabel });
  }

  // =========================
  // Clinic data
  // =========================

  getClinicName(): string {
    if (!this.clinic) {
      return '';
    }

    return this.isAr
      ? (
          this.clinic.name_ar ||
          this.clinic.warehousename_ar ||
          this.clinic.warehouse_name_ar ||
          this.clinic.name ||
          this.clinic.name_en ||
          this.clinic.warehousename_en ||
          this.clinic.warehouse_name_en ||
          this.t('bbClinic.common.clinic')
        )
      : (
          this.clinic.name_en ||
          this.clinic.warehousename_en ||
          this.clinic.warehouse_name_en ||
          this.clinic.name ||
          this.clinic.name_ar ||
          this.clinic.warehousename_ar ||
          this.clinic.warehouse_name_ar ||
          this.t('bbClinic.common.clinic')
        );
  }

  getClinicDescription(): string {
    if (!this.clinic) {
      return '';
    }

    return this.isAr
      ? (
          this.clinic.description_ar ||
          this.clinic.warehouse_description_ar ||
          this.clinic.description ||
          this.clinic.description_en ||
          this.clinic.warehouse_description_en ||
          ''
        )
      : (
          this.clinic.description_en ||
          this.clinic.warehouse_description_en ||
          this.clinic.description ||
          this.clinic.description_ar ||
          this.clinic.warehouse_description_ar ||
          ''
        );
  }


  getClinicAddress(): string {
    return (
      this.clinic?.location ||
      this.clinic?.address_ar ||
      this.clinic?.warehouse_address_ar ||
      this.clinic?.address_en ||
      this.clinic?.warehouse_address_en ||
      this.clinic?.address ||
      ''
    );
  }

  getPhone(): string {
    return (
      this.clinic?.phonenumber ||
      this.clinic?.phone_number ||
      this.clinic?.warehouse_phone ||
      this.clinic?.phone ||
      ''
    );
  }

  getAnotherPhone(): string {
    return (
      this.clinic?.another_phonenumber ||
      this.clinic?.another_phone_number ||
      this.clinic?.second_phone ||
      ''
    );
  }

  // =========================
  // Service data
  // =========================

  getServiceName(service: any): string {
    return this.isAr
      ? (
          service?.name_ar ||
          service?.service_name_ar ||
          service?.title_ar ||
          service?.name ||
          service?.name_en ||
          service?.service_name_en ||
          '-'
        )
      : (
          service?.name_en ||
          service?.service_name_en ||
          service?.title_en ||
          service?.name ||
          service?.name_ar ||
          service?.service_name_ar ||
          '-'
        );
  }

  getServiceDescription(service: any): string {
    return this.isAr
      ? (
          service?.description_ar ||
          service?.service_description_ar ||
          service?.description ||
          service?.description_en ||
          service?.service_description_en ||
          ''
        )
      : (
          service?.description_en ||
          service?.service_description_en ||
          service?.description ||
          service?.description_ar ||
          service?.service_description_ar ||
          ''
        );
  }

  getServicePrice(service: any): number {
    const price =
      service?.pricea ??
      service?.selling_price ??
      service?.service_price ??
      service?.price ??
      0;

    return Number(price) || 0;
  }

  getPetLabel(service: any): string {
    switch (String(service?.pet)) {
      case '0':
        return this.t('bbClinic.pets.cats');

      case '1':
        return this.t('bbClinic.pets.dogs');

      case '2':
        return this.t('bbClinic.pets.birds');

      case '4':
        return this.t('bbClinic.pets.catsDogs');

      default:
        return this.t('bbClinic.pets.pets');
    }
  }

  formatMoney(value: any): string {
    return Number(value || 0).toLocaleString(
      this.isAr ? 'ar-EG' : 'en-US',
      {
        maximumFractionDigits: 2
      }
    );
  }


  private resolveMediaUrl(
    value: any,
    fallback: string
  ): string {
    if (!value) {
      return fallback;
    }

    let path = String(value)
      .trim()
      .replace(/\\/g, '/');

    if (!path) {
      return fallback;
    }

    // Already a complete URL.
    if (
      /^https?:\/\//i.test(path) ||
      /^data:/i.test(path) ||
      /^blob:/i.test(path)
    ) {
      return path;
    }

    // Protocol-relative URL.
    if (path.startsWith('//')) {
      return `https:${path}`;
    }

    // Angular local assets.
    path = path.replace(/^\/+/, '');

    if (
      path.startsWith('assets/') ||
      path.startsWith('./assets/')
    ) {
      return path.replace(/^\.\//, '');
    }

    const backendBaseUrl =
      this.getBackendBaseUrl();

    if (!backendBaseUrl) {
      return fallback;
    }

    // Remove public/ if the database contains it.
    path = path.replace(/^public\//i, '');

    // Path already contains storage/.
    if (path.startsWith('storage/')) {
      return `${backendBaseUrl}/${path}`;
    }

    // Database path example:
    // pet_services/file.webp
    // warehouses/file.webp
    return `${backendBaseUrl}/storage/${path}`;
  }

  private getBackendBaseUrl(): string {
    const serverUrl =
      String(environment.Server_URL || '')
        .trim()
        .replace(/\/+$/, '');

    if (!serverUrl) {
      return '';
    }

    /*
     * Example:
     * https://backendproduction.blueboxpet.com/api
     *
     * Becomes:
     * https://backendproduction.blueboxpet.com
     */
    return serverUrl.replace(
      /\/api(?:\/.*)?$/i,
      ''
    );
  }

fixImage(url: any): string {
  const defaultImage = 'assets/logo-01.png';

  if (!url) {
    return defaultImage;
  }

  const cleanUrl = String(url).trim();

  // Ù„Ùˆ Ø§Ù„Ø±Ø§Ø¨Ø· ÙƒØ§Ù…Ù„ Ø¨Ø§Ù„ÙØ¹Ù„
  if (
    cleanUrl.startsWith('http://') ||
    cleanUrl.startsWith('https://')
  ) {
    return cleanUrl;
  }

  // Ù†Ø´ÙŠÙ„ Ø£ÙŠ slash Ù…Ù† Ø§Ù„Ø¨Ø¯Ø§ÙŠØ©
  const cleanPath = cleanUrl.replace(/^\/+/, '');

  return `https://backendproduction.blueboxpet.com/storage/app/public/${cleanPath}`;
}

getClinicLogo(): string {
  const logo =
    this.clinic?.logo_url ||
    this.clinic?.brand_logo_url ||
    this.clinic?.warehouse_logo_url ||
    this.clinic?.brand_logo ||
    this.clinic?.warehouse_logo ||
    this.clinic?.logo ||
    this.clinic?.image_url ||
    this.clinic?.image;

  return this.fixImage(logo);
}

getImage(item: any): string {
  const image =
    item?.image_url ||
    item?.service_image_url ||
    item?.image ||
    item?.service_image ||
    item?.photo ||
    item?.thumbnail ||
    item?.logo_url ||
    item?.logo;

  return this.fixImage(image);
}

onImageError(event: Event): void {
  const image = event.target as HTMLImageElement;

  image.onerror = null;
  image.src = 'assets/logo-01.png';
}

  // =========================
  // Contact links
  // =========================

  phoneHref(phone: any): string | null {
    if (!phone) {
      return null;
    }

    const cleanPhone =
      String(phone)
        .trim()
        .replace(/\s+/g, '');

    return cleanPhone
      ? `tel:${cleanPhone}`
      : null;
  }

  whatsappHref(phone: any): string | null {
    if (!phone) {
      return null;
    }

    let cleanPhone = String(phone).trim().replace(/[^\d+]/g, '');

    if (!cleanPhone) {
      return null;
    }

    if (cleanPhone.startsWith('+')) {
      cleanPhone = cleanPhone.slice(1);
    } else if (cleanPhone.startsWith('00')) {
      cleanPhone = cleanPhone.slice(2);
    } else if (cleanPhone.startsWith('0')) {
      cleanPhone = `20${cleanPhone.slice(1)}`;
    }

    return `https://wa.me/${cleanPhone}`;
  }

  externalHref(link: any): string | null {
    if (!link) {
      return null;
    }

    const cleanLink =
      String(link).trim();

    if (!cleanLink) {
      return null;
    }

    if (
      cleanLink.startsWith('http://') ||
      cleanLink.startsWith('https://')
    ) {
      return cleanLink;
    }

    return `https://${cleanLink}`;
  }

  // =========================
  // Date helpers
  // =========================

  private syncBookingDateTime(): void {
    if (!this.bookingDateOnly) {
      this.bookingForm.patchValue({ booking_date: '' }, { emitEvent: false });
      return;
    }

    this.bookingForm.patchValue(
      {
        booking_date: `${this.bookingDateOnly}T${this.bookingTime}`,
      },
      { emitEvent: false }
    );

    const bookingDateControl = this.bookingForm.get('booking_date');
    if (bookingDateControl?.hasError('pastDate') && this.isSelectedBookingTimeValid()) {
      bookingDateControl.setErrors(null);
    }
  }

  private to24Hour(hour12: number, period: 'am' | 'pm'): string {
    let hour = Number(hour12);
    if (period === 'am' && hour === 12) hour = 0;
    if (period === 'pm' && hour !== 12) hour += 12;
    return String(hour).padStart(2, '0');
  }

  private parseDateOnly(value: string): Date | null {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private isSameDate(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
  }

  private normalizeDateTime(
    value: string
  ): string {
    if (!value) {
      return value;
    }

    return value.length === 16
      ? `${value}:00`
      : value;
  }

  private formatDateTimeForInput(
    date: Date
  ): string {
    const pad = (
      value: number
    ): string =>
      String(value).padStart(2, '0');

    return [
      date.getFullYear(),
      '-',
      pad(date.getMonth() + 1),
      '-',
      pad(date.getDate()),
      'T',
      pad(date.getHours()),
      ':',
      pad(date.getMinutes())
    ].join('');
  }

  private formatDateOnly(date: Date): string {
    const pad = (
      value: number
    ): string =>
      String(value).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private toArabicDigits(value: string | number): string {
    return String(value).replace(/\d/g, (digit) => 'Ã™Â Ã™Â¡Ã™Â¢Ã™Â£Ã™Â¤Ã™Â¥Ã™Â¦Ã™Â§Ã™Â¨Ã™Â©'[Number(digit)]);
  }
}
